import React, { useState, useRef, useEffect, useCallback } from 'react'
import { API_CONFIG } from '../config/constants'
import { fetchRoleTypes } from '../utils/api'
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue'
import discoveryConfigService from '../services/DiscoveryConfigService'

/**
 * Custom hook for managing role type selection and related functionality
 * @param {Function} setMessages - Function to update messages state
 * @param {Function} saveCurrentSession - Function to save the current session
 * @param {Function} setSessionName - Function to update session name
 * @returns {Object} Role type management state and functions
 */
const useRoleTypeManager = (
  setMessages,
  saveCurrentSession,
  setSessionName
) => {
  const [selectedRoleTypes, setSelectedRoleTypes] = useState([])
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [convoStarted, setConvoStarted] = useState(false)
  const sessionSavedRef = useRef(false)
  const [roleTypesCache, setRoleTypesCache] = useState([])
  const [currentDiscoveryState, setCurrentDiscoveryState] = useState(null)

  // Fetch role types from the server when the component mounts
  useEffect(() => {
    const getRoleTypes = async () => {
      try {
        const data = await fetchRoleTypes()
        if (data && data.roleTypes && data.roleTypes.length > 0) {
          setRoleTypesCache(data)
        }
      } catch (error) {
        console.error('Error fetching role types:', error)
      }
    }

    getRoleTypes()
  }, [])
  /**
   * Start the intake process by showing the role selection modal
   */
  const handleStartIntake = () => {
    setShowRoleModal(true)
  }

  /**
   * Handle role type selection
   * @param {Array} roleTypes - Selected role types
   * @param {string} userId - User ID for fetching custom sequences
   */
  const handleRoleTypeSelect = async (roleTypes, userIdOrOptions = null, maybeOptions = {}) => {
    let userId = userIdOrOptions;
    let options = maybeOptions;
    if (userIdOrOptions && typeof userIdOrOptions === 'object' && !Array.isArray(userIdOrOptions)) {
      options = userIdOrOptions;
      userId = null;
    }
    const silent = !!options.silent;
    // Now set convoStarted to true after role type is selected
    setConvoStarted(true)
    setShowRoleModal(false) // Hide the modal
    setSelectedRoleTypes(roleTypes)

    // Get the selected role type
    const roleType = roleTypes[0] // Since we're only supporting single selection for now

    // Initialize discovery state based on the selected role type
    let discoveryOrder
    let sequenceSource = 'fallback'

    try {
      // First, try to get custom sequence from DiscoveryConfigService
      if (userId) {
        console.log(`Fetching effective sequence for user ${userId} and role type ${roleType}`)
        const effectiveSequence = await discoveryConfigService.getEffectiveSequence(userId, roleType)
        
        if (effectiveSequence && effectiveSequence.topics) {
          // Extract topic keys from custom sequence
          discoveryOrder = effectiveSequence.topics
            .filter(topic => topic.is_enabled)
            .sort((a, b) => a.order_index - b.order_index)
            .map(topic => topic.topic_key)
          
          sequenceSource = 'custom'
          console.log(`Using custom discovery sequence for ${roleType}:`, discoveryOrder)
        }
      }
    } catch (error) {
      console.warn(`Error fetching custom sequence for ${roleType}:`, error)
      // Continue to fallback options
    }

    // Fallback to API discovery order if custom sequence not available
    if (!discoveryOrder) {
      // Find the selected role type in the cache to get the discovery order
      console.log('🔍 DEBUG: roleTypesCache:', roleTypesCache);
      console.log('🔍 DEBUG: roleTypesCache.roleTypes:', roleTypesCache.roleTypes);
      console.log('🔍 DEBUG: roleType:', roleType);
      
      const selectedRoleTypeData = roleTypesCache.roleTypes ? roleTypesCache.roleTypes.find(rt => rt.id === roleType) : null
      console.log('🔍 DEBUG: selectedRoleTypeData:', selectedRoleTypeData);

      if (selectedRoleTypeData && selectedRoleTypeData.discovery_order) {
        // Use the discovery_order from the API response
        discoveryOrder = selectedRoleTypeData.discovery_order
        sequenceSource = 'api'
        console.log(`Using discovery_order from API for ${roleType}:`, discoveryOrder)
      } else if (selectedRoleTypeData && selectedRoleTypeData.discoveryOrder) {
        // Fallback to discoveryOrder if discovery_order is not available
        discoveryOrder = selectedRoleTypeData.discoveryOrder
        sequenceSource = 'api_legacy'
        console.log(`Using discoveryOrder from API for ${roleType}:`, discoveryOrder)
      }
    }

    // Final fallback discovery orders if not found in API or custom sequences
    if (!discoveryOrder) {
      const fallbackDiscoveryOrders = {
        TECHNICAL: [
          'skills',
          'company_size', 
          'business_model',
          'education',
          'company_stage',
          'companies',
          'experience',
          'location',
          'title',
          'candidate_preview'
        ],
        CREATIVE: [
          'title',
          'location',
          'companies',
          'skills',
          'experience',
          'education',
          'candidate_preview'
        ],
        LEADERSHIP: [
          'title',
          'companies',
          'location',
          'experience',
          'education',
          'skills',
          'candidate_preview'
        ],
        INTERPERSONAL: [
          'title',
          'location',
          'companies',
          'experience',
          'skills',
          'education',
          'candidate_preview'
        ],
        OPERATIONAL: [
          'title',
          'location',
          'companies',
          'experience',
          'education',
          'skills',
          'candidate_preview'
        ]
      }

      discoveryOrder = fallbackDiscoveryOrders[roleType] || [
        'title',
        'candidate_preview',
        'experience',
        'skills',
        'location',
        'companies',
        'education'
      ]
      sequenceSource = 'hardcoded_fallback'
      console.log(`Using fallback discovery order for ${roleType}:`, discoveryOrder)
    }

    console.log(`Discovery sequence source for ${roleType}: ${sequenceSource}`)

    // Set the current discovery state
    // Get the first topic from the discovery order as the current topic
    const currentDiscoTopic =
      discoveryOrder.length > 0 ? discoveryOrder[0] : null

    const newDiscoveryState = {
      roleType: roleType,
      discoveryOrder: discoveryOrder,
      currentDiscoTopic: currentDiscoTopic,
      completedTopics: []
    }

    setCurrentDiscoveryState(newDiscoveryState)

    // Initialize central discovery queue for the first topic
    if (currentDiscoTopic) {
      console.log('🎯 ROLE TYPE MANAGER: Initializing central queue for first topic:', currentDiscoTopic)
      // Note: We'll initialize the queue when the session is saved and we have a sessionId
      // This will be handled in the saveCurrentSession callback below
    }

    // Define role type display names for session naming and display
    const roleTypeSessionNames = {
      TECHNICAL: 'Technical Role',
      CREATIVE: 'Creative Role',
      LEADERSHIP: 'Leadership Role',
      INTERPERSONAL: 'Interpersonal Role',
      OPERATIONAL: 'Operational Role'
    }

    const roleTypeDisplayNames = {
      TECHNICAL: 'a skill-based role',
      CREATIVE: 'a creative/strategic role',
      LEADERSHIP: 'a leadership role',
      INTERPERSONAL: 'a soft-skill focused role',
      OPERATIONAL: 'a high-volume operational role'
    }

    // Generate a default session name with date
    const currentDate = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    const defaultSessionName = `${
      roleTypeSessionNames[roleType] || 'New Role'
    } - ${currentDate}`

    // Update the session name in the ChatSessionManager
    if (setSessionName) {
      setSessionName(defaultSessionName)
      console.log('Setting session name to:', defaultSessionName)
    }

    // Get the display name for the selected role type
    const roleTypeDisplayName =
      roleTypeDisplayNames[roleType] || 'this role type'

    // If selecting silently (pre-11Labs), initialize discovery without injecting chat messages
    if (silent) {
      sessionSavedRef.current = true;
      try {
        const savedSession = await saveCurrentSession(
          defaultSessionName,
          null,
          roleType,
          newDiscoveryState
        );
        if (savedSession && savedSession.id) {
          try {
            await centralDiscoveryQueue.initialize(savedSession.id, []);
            console.log('✅ ROLE TYPE MANAGER (silent): Central discovery queue initialized');
          } catch (error) {
            console.error('❌ ROLE TYPE MANAGER (silent): Error initializing central discovery queue:', error);
          }
        }
      } catch (e) {
        console.error('❌ ROLE TYPE MANAGER (silent): Error saving session:', e);
      }
      return;
    }

    // Prepare the user message
    const userMessage = {
      role: 'User',
      content: `The role type we are hiring for is ${roleTypeDisplayName}.`
    }

    // Reset the session saved flag
    sessionSavedRef.current = false

    // Add only the user message - the server will add the AI message
    setMessages(prev => {
      const updatedMessages = [...prev, userMessage]
      return updatedMessages
    })

    // Save the session outside of the setMessages callback
    // This ensures it's only called once
    setTimeout(() => {
      // Only save if we haven't already saved this session
      if (!sessionSavedRef.current) {
        sessionSavedRef.current = true
        console.log('Saving session with role type:', roleType)

        saveCurrentSession(
          defaultSessionName,
          null,
          roleType,
          newDiscoveryState
        ).then(async savedSession => {
          if (savedSession && savedSession.id) {
            // Initialize central discovery queue with the session ID and role type config
            console.log('🎯 ROLE TYPE MANAGER: Initializing central discovery queue with session ID:', savedSession.id)
            try {
              await centralDiscoveryQueue.initialize(savedSession.id, [])
              console.log('✅ ROLE TYPE MANAGER: Central discovery queue initialized successfully')
            } catch (error) {
              console.error('❌ ROLE TYPE MANAGER: Error initializing central discovery queue:', error)
            }

            // Call the new API endpoint to add the next steps message
            fetch(
              `${API_CONFIG.url}/api/role-type-selection/${savedSession.userId}/${savedSession.id}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ roleType })
              }
            )
              .then(response => response.json())
              .then(data => {
                console.log('Added next steps message to session:', data)
                // Update the messages with the new AI message
                if (data.history && data.history.messages) {
                  setMessages(data.history.messages)
                }
              })
              .catch(error => {
                console.error('Error adding next steps message:', error)
              })
          }
        })
      }
    }, 100)
  }

  /**
   * Handle starting a new conversation
   */
  const handleStartConversation = () => {
    // Only set the initial greeting message if there are no messages already
    return messages => {
      if (messages.length === 0) {
        const userName = 'there' // Default fallback
        let initialMessage = `Hey ${userName} 👋\n\nLet's begin your intake meeting and align your team on the requirements.\nI'm here to guide you through the process!\n\n`

        initialMessage += `✅ Live collaboration: Complete this process with your hiring manager.\n📋 Search strategy card (on the right): I'll fill this in as we chat.\n📋 How we'll work together: As we progress through the discussion, I'll confirm what I've heard before updating the search strategy on the right.\n🚀 Outcome: Once complete, we have the search strategy for this role.\n\nReady to begin?`

        return [
          {
            role: 'AI',
            content: initialMessage
          }
        ]
      }
      return messages
    }
  }

  /**
   * Reset the role type selection state
   */
  const resetRoleTypeSelection = () => {
    setConvoStarted(false)
    setSelectedRoleTypes([])
    setCurrentDiscoveryState(null) // Reset discovery state to ensure clean start
  }

  /**
   * Update the discovery state
   * @param {Object} newDiscoveryState - New discovery state
   */
  const updateDiscoveryState = newDiscoveryState => {
    console.log('DISCOVERY STATE UPDATE - START:', {
      timestamp: new Date().toISOString(),
      function: 'updateDiscoveryState'
    })

    // Log the current and new discovery states
    console.log('DISCOVERY STATE UPDATE - STATES:', {
      current: currentDiscoveryState
        ? {
            roleType: currentDiscoveryState.roleType,
            currentTopic: currentDiscoveryState.currentDiscoTopic,
            completedTopics: currentDiscoveryState.completedTopics,
            discoveryOrder: currentDiscoveryState.discoveryOrder
          }
        : null,
      new: newDiscoveryState
        ? {
            roleType: newDiscoveryState.roleType,
            currentTopic: newDiscoveryState.currentDiscoTopic,
            completedTopics: newDiscoveryState.completedTopics,
            discoveryOrder: newDiscoveryState.discoveryOrder
          }
        : null
    })

    // Check if the discovery state has changed
    const hasChanged =
      JSON.stringify(currentDiscoveryState) !==
      JSON.stringify(newDiscoveryState)
    console.log('DISCOVERY STATE UPDATE - HAS CHANGED:', hasChanged)

    if (hasChanged) {
      console.log('DISCOVERY STATE UPDATE - UPDATING STATE')

      // Update the state
      setCurrentDiscoveryState(newDiscoveryState)

      // Save the updated discovery state to the session
      if (saveCurrentSession) {
        const roleType =
          selectedRoleTypes.length > 0 ? selectedRoleTypes[0] : null
        console.log('DISCOVERY STATE UPDATE - SAVING TO SESSION:', {
          roleType,
          newState: newDiscoveryState
        })

        saveCurrentSession(null, null, roleType, newDiscoveryState)
          .then(() => {
            console.log(
              'DISCOVERY STATE UPDATE - SAVED TO SESSION SUCCESSFULLY'
            )
          })
          .catch(error => {
            console.error(
              'DISCOVERY STATE UPDATE - ERROR SAVING TO SESSION:',
              error
            )
          })
      } else {
        console.log(
          'DISCOVERY STATE UPDATE - NO SAVE SESSION FUNCTION AVAILABLE'
        )
      }
    } else {
      console.log('DISCOVERY STATE UPDATE - NO CHANGES, SKIPPING UPDATE')
    }

    console.log('DISCOVERY STATE UPDATE - COMPLETE')
  }

  /**
   * Load role type and discovery state from a session
   * @param {Object} session - Session object
   */
  const loadSessionState = useCallback(async (session) => {
    console.log('SESSION LOAD - START:', {
      timestamp: new Date().toISOString(),
      function: 'loadSessionState',
      sessionId: session?.id || 'unknown'
    })

    if (!session) {
      console.warn('SESSION LOAD - ERROR: No session provided')
      return
    }

    console.log('SESSION LOAD - SESSION DATA:', {
      id: session.id,
      name: session.name,
      roleType: session.roleType,
      roleTypes: session.roleTypes,
      hasDiscoveryState: !!session.discoveryState,
      hasDiscoveryStatus: !!session.discoveryStatus
    })

    // Load role type - handle both roleType and roleTypes
    if (session.roleType) {
      console.log('SESSION LOAD - ROLE TYPE:', session.roleType)
      setSelectedRoleTypes([session.roleType])
      setConvoStarted(true)
    } else if (
      session.roleTypes &&
      Array.isArray(session.roleTypes) &&
      session.roleTypes.length > 0
    ) {
      console.log('SESSION LOAD - ROLE TYPES:', session.roleTypes)
      setSelectedRoleTypes(session.roleTypes)
      setConvoStarted(true)
    } else {
      console.warn('SESSION LOAD - WARNING: No role type found in session')
    }

    // Load discovery state - handle both discoveryState and discoveryStatus
    let discoveryState = null

    if (session.discoveryState) {
      discoveryState = session.discoveryState
      console.log('SESSION LOAD - DISCOVERY STATE:', {
        roleType: discoveryState.roleType,
        currentTopic: discoveryState.currentDiscoTopic,
        completedTopics: discoveryState.completedTopics,
        discoveryOrder: discoveryState.discoveryOrder
      })
    } else if (session.discoveryStatus) {
      discoveryState = session.discoveryStatus
      console.log('SESSION LOAD - DISCOVERY STATUS:', {
        roleType: discoveryState.roleType,
        currentTopic: discoveryState.currentDiscoTopic,
        completedTopics: discoveryState.completedTopics,
        discoveryOrder: discoveryState.discoveryOrder
      })
    } else {
      console.warn(
        'SESSION LOAD - WARNING: No discovery state found in session'
      )
    }

    // Fix empty discovery order by re-initializing from roleTypes
    if (discoveryState && (!discoveryState.discoveryOrder || discoveryState.discoveryOrder.length === 0)) {
      console.log('SESSION LOAD - DISCOVERY ORDER IS EMPTY, RE-INITIALIZING FROM ROLE TYPES');
      
      // Re-initialize discovery order from roleTypes just like in handleRoleTypeSelect
      let newDiscoveryOrder = null;
      
      if (discoveryState.roleType && roleTypesCache.roleTypes) {
        const selectedRoleTypeData = roleTypesCache.roleTypes.find(rt => rt.id === discoveryState.roleType);
        if (selectedRoleTypeData && selectedRoleTypeData.discovery_order) {
          newDiscoveryOrder = selectedRoleTypeData.discovery_order;
          console.log(`SESSION LOAD - Re-initialized discovery_order from API for ${discoveryState.roleType}:`, newDiscoveryOrder);
        }
      }
      
      // Fallback to hardcoded discovery order if not found in API
      if (!newDiscoveryOrder) {
        const fallbackDiscoveryOrders = {
          'SKILL FOCUSSED': ['skills', 'education', 'companies', 'experience', 'location', 'title', 'candidate_preview'],
          'TECHNICAL': ['skills', 'education', 'companies', 'experience', 'location', 'title', 'candidate_preview'],
          'CREATIVE': ['title', 'location', 'companies', 'skills', 'experience', 'education', 'candidate_preview'],
          'LEADERSHIP': ['title', 'companies', 'location', 'experience', 'education', 'skills', 'candidate_preview'],
          'INTERPERSONAL': ['title', 'location', 'companies', 'experience', 'skills', 'education', 'candidate_preview'],
          'OPERATIONAL': ['title', 'location', 'companies', 'experience', 'education', 'skills', 'candidate_preview']
        };
        
        newDiscoveryOrder = fallbackDiscoveryOrders[discoveryState.roleType] || fallbackDiscoveryOrders['SKILL FOCUSSED'];
        console.log(`SESSION LOAD - Using fallback discovery order for ${discoveryState.roleType}:`, newDiscoveryOrder);
      }
      
      // Update the discovery state with the new discovery order
      discoveryState.discoveryOrder = newDiscoveryOrder;
      
      // If currentDiscoTopic is null, set it to the first topic in the discovery order
      if (!discoveryState.currentDiscoTopic && newDiscoveryOrder && newDiscoveryOrder.length > 0) {
        discoveryState.currentDiscoTopic = newDiscoveryOrder[0];
        console.log(`SESSION LOAD - Set currentDiscoTopic to first topic: ${discoveryState.currentDiscoTopic}`);
      }
    }
    
    // Update the discovery order to include candidate_preview if it's missing
    if (discoveryState && discoveryState.discoveryOrder) {
      // Check if candidate_preview is missing from the discovery order
      if (!discoveryState.discoveryOrder.includes('candidate_preview')) {
        console.log(
          'SESSION LOAD - ADDING CANDIDATE_PREVIEW TO DISCOVERY ORDER'
        )

        // Find the index of the title topic
        const titleIndex = discoveryState.discoveryOrder.indexOf('title')

        if (titleIndex !== -1) {
          // Insert candidate_preview after title
          const newDiscoveryOrder = [...discoveryState.discoveryOrder]
          newDiscoveryOrder.splice(titleIndex + 1, 0, 'candidate_preview')
          discoveryState.discoveryOrder = newDiscoveryOrder

          console.log(
            'SESSION LOAD - UPDATED DISCOVERY ORDER:',
            discoveryState.discoveryOrder
          )

          // If the current topic is after title, we need to update it
          if (
            discoveryState.currentDiscoTopic &&
            titleIndex !== -1 &&
            discoveryState.discoveryOrder.indexOf(
              discoveryState.currentDiscoTopic
            ) > titleIndex
          ) {
            // The current topic is after title, so we need to update it
            console.log(
              'SESSION LOAD - CURRENT TOPIC IS AFTER TITLE, UPDATING COMPLETED TOPICS'
            )

            // If title is in completedTopics, add candidate_preview to completedTopics
            if (
              discoveryState.completedTopics &&
              discoveryState.completedTopics.includes('title')
            ) {
              console.log(
                'SESSION LOAD - TITLE IS COMPLETED, ADDING CANDIDATE_PREVIEW TO COMPLETED TOPICS'
              )
              discoveryState.completedTopics = [
                ...discoveryState.completedTopics,
                'candidate_preview'
              ]
            }
          }
        }
      }
    }

    // If we have a discovery state but no currentDiscoTopic, initialize it
    if (discoveryState) {
      console.log('SESSION LOAD - INITIALIZING DISCOVERY STATE')

      // If currentDiscoTopic is missing but we have a discovery order, initialize it
      if (
        !discoveryState.currentDiscoTopic &&
        discoveryState.discoveryOrder &&
        discoveryState.discoveryOrder.length > 0
      ) {
        console.log(
          'SESSION LOAD - NO CURRENT TOPIC, INITIALIZING FROM DISCOVERY ORDER'
        )

        // If we have completed topics, set the current topic to the next one in the order
        if (
          discoveryState.completedTopics &&
          discoveryState.completedTopics.length > 0
        ) {
          console.log(
            'SESSION LOAD - HAS COMPLETED TOPICS:',
            discoveryState.completedTopics
          )

          const lastCompletedTopic =
            discoveryState.completedTopics[
              discoveryState.completedTopics.length - 1
            ]
          const lastCompletedIndex = discoveryState.discoveryOrder.findIndex(
            topic => topic === lastCompletedTopic
          )

          console.log('SESSION LOAD - LAST COMPLETED TOPIC:', {
            topic: lastCompletedTopic,
            index: lastCompletedIndex
          })

          // If we found the last completed topic in the discovery order
          if (
            lastCompletedIndex !== -1 &&
            lastCompletedIndex < discoveryState.discoveryOrder.length - 1
          ) {
            // Set the current topic to the next one in the order
            discoveryState.currentDiscoTopic =
              discoveryState.discoveryOrder[lastCompletedIndex + 1]
            console.log(
              'SESSION LOAD - INITIALIZED TO NEXT TOPIC:',
              discoveryState.currentDiscoTopic
            )
          } else {
            // If we're at the end of the discovery order, set currentDiscoTopic to null
            discoveryState.currentDiscoTopic = null
            console.log('SESSION LOAD - ALL TOPICS COMPLETED, SETTING TO NULL')
          }
        } else {
          // If no completed topics, set the current topic to the first one in the order
          discoveryState.currentDiscoTopic = discoveryState.discoveryOrder[0]
          discoveryState.completedTopics = []
          console.log(
            'SESSION LOAD - NO COMPLETED TOPICS, INITIALIZED TO FIRST TOPIC:',
            discoveryState.currentDiscoTopic
          )
        }
      } else {
        console.log(
          'SESSION LOAD - CURRENT TOPIC ALREADY SET:',
          discoveryState.currentDiscoTopic
        )
      }

      // Set the discovery state
      console.log('SESSION LOAD - SETTING DISCOVERY STATE:', {
        roleType: discoveryState.roleType,
        currentTopic: discoveryState.currentDiscoTopic,
        completedTopics: discoveryState.completedTopics,
        discoveryOrder: discoveryState.discoveryOrder
      })

      setCurrentDiscoveryState(discoveryState)

      // Initialize central discovery queue with the session ID if available
      if (session.id) {
        console.log('🎯 ROLE TYPE MANAGER: Initializing central discovery queue with loaded session ID:', session.id)
        try {
          await centralDiscoveryQueue.initialize(session.id, [])
          console.log('✅ ROLE TYPE MANAGER: Central discovery queue initialized successfully for loaded session')
        } catch (error) {
          console.error('❌ ROLE TYPE MANAGER: Error initializing central discovery queue for loaded session:', error)
        }
      }
    }

    console.log('SESSION LOAD - COMPLETE')
  }, [setSelectedRoleTypes, setConvoStarted, setCurrentDiscoveryState])

  /**
   * Direct topic transition callback - more elegant than using onSendText
   * @param {string} targetTopic - The topic to transition to (optional, if not provided, advances to next)
   * @param {Object} options - Additional options for the transition
   * @returns {Object} The updated discovery state
   */
  const transitionToTopic = (targetTopic = null, options = {}) => {
    console.log('DIRECT TOPIC TRANSITION - START:', {
      timestamp: new Date().toISOString(),
      function: 'transitionToTopic',
      targetTopic,
      options
    })

    console.log('DIRECT TOPIC TRANSITION - CURRENT STATE:', {
      currentDiscoveryState: currentDiscoveryState
        ? {
            roleType: currentDiscoveryState.roleType,
            currentTopic: currentDiscoveryState.currentDiscoTopic,
            completedTopics: currentDiscoveryState.completedTopics,
            discoveryOrder: currentDiscoveryState.discoveryOrder
          }
        : null
    })

    if (!currentDiscoveryState) {
      console.warn('DIRECT TOPIC TRANSITION - ERROR: No current discovery state')
      return null
    }

    const { roleType, discoveryOrder, currentDiscoTopic, completedTopics } = currentDiscoveryState

    // If no target topic is specified, advance to the next topic in the discovery order
    if (!targetTopic) {
      return advanceToNextTopic()
    }

    // If target topic is specified, transition directly to that topic
    console.log('DIRECT TOPIC TRANSITION - TRANSITIONING TO:', targetTopic)

    // Mark the current topic as completed if specified in options
    let updatedCompletedTopics = [...completedTopics]
    if (options.markCurrentAsCompleted && currentDiscoTopic && !completedTopics.includes(currentDiscoTopic)) {
      updatedCompletedTopics.push(currentDiscoTopic)
      console.log('DIRECT TOPIC TRANSITION - MARKING CURRENT TOPIC AS COMPLETED:', currentDiscoTopic)
    }

    // Create the updated discovery state
    const updatedDiscoveryState = {
      roleType,
      discoveryOrder,
      currentDiscoTopic: targetTopic,
      completedTopics: updatedCompletedTopics
    }

    console.log('DIRECT TOPIC TRANSITION - NEW STATE:', updatedDiscoveryState)

    // Update the discovery state
    try {
      updateDiscoveryState(updatedDiscoveryState)
      console.log('DIRECT TOPIC TRANSITION - SUCCESS: State updated')
    } catch (error) {
      console.error('DIRECT TOPIC TRANSITION - ERROR: Failed to update state', error)
    }

    console.log('DIRECT TOPIC TRANSITION - COMPLETE:', {
      previousTopic: currentDiscoTopic,
      newTopic: targetTopic,
      timestamp: new Date().toISOString()
    })

    return updatedDiscoveryState
  }

  /**
   * Advance to the next discovery topic
   * @returns {Object} The updated discovery state
   */
  const advanceToNextTopic = () => {
    console.log('TOPIC ADVANCEMENT - START:', {
      timestamp: new Date().toISOString(),
      function: 'advanceToNextTopic'
    })

    console.log('TOPIC ADVANCEMENT - CURRENT STATE:', {
      currentDiscoveryState: currentDiscoveryState
        ? {
            roleType: currentDiscoveryState.roleType,
            currentTopic: currentDiscoveryState.currentDiscoTopic,
            completedTopics: currentDiscoveryState.completedTopics,
            discoveryOrder: currentDiscoveryState.discoveryOrder
          }
        : null
    })

    if (!currentDiscoveryState) {
      console.warn('TOPIC ADVANCEMENT - ERROR: No current discovery state')
      return null
    }

    const { roleType, discoveryOrder, currentDiscoTopic, completedTopics } =
      currentDiscoveryState
    console.log('TOPIC ADVANCEMENT - DETAILS:', {
      roleType,
      discoveryOrder,
      currentDiscoTopic,
      completedTopics
    })

    // If there's no current topic, we can't advance
    if (!currentDiscoTopic) {
      console.warn('TOPIC ADVANCEMENT - ERROR: No current topic')
      return currentDiscoveryState
    }

    // Create a copy of the completed topics array and add the current topic
    const updatedCompletedTopics = [...completedTopics, currentDiscoTopic]
    console.log('TOPIC ADVANCEMENT - MARKING COMPLETED:', {
      completingTopic: currentDiscoTopic,
      updatedCompletedTopics
    })

    // Find the index of the current topic in the discovery order
    const currentIndex = discoveryOrder.indexOf(currentDiscoTopic)
    console.log('TOPIC ADVANCEMENT - CURRENT INDEX:', {
      currentTopic: currentDiscoTopic,
      currentIndex,
      totalTopics: discoveryOrder.length
    })

    // If the current topic is not in the discovery order, we can't advance
    if (currentIndex === -1) {
      console.warn(
        `TOPIC ADVANCEMENT - ERROR: Current topic "${currentDiscoTopic}" not found in discovery order`
      )
      return currentDiscoveryState
    }

    // If we're already at the last topic, we're done
    if (currentIndex === discoveryOrder.length - 1) {
      console.log('TOPIC ADVANCEMENT - COMPLETING ALL TOPICS')

      // Update the discovery state to mark all topics as completed
      const updatedDiscoveryState = {
        roleType,
        discoveryOrder,
        currentDiscoTopic: null, // No current topic since we're done
        completedTopics: updatedCompletedTopics
      }

      console.log('TOPIC ADVANCEMENT - FINAL STATE:', updatedDiscoveryState)

      // Update the discovery state
      updateDiscoveryState(updatedDiscoveryState)

      return updatedDiscoveryState
    }

    // Get the next topic from the discovery order
    const nextTopic = discoveryOrder[currentIndex + 1]
    console.log('TOPIC ADVANCEMENT - NEXT TOPIC:', {
      currentTopic: currentDiscoTopic,
      nextTopic,
      currentIndex,
      nextIndex: currentIndex + 1,
      discoveryOrder
    })

    // Add more detailed logging for debugging
    console.log(
      'TOPIC ADVANCEMENT - DISCOVERY ORDER:',
      JSON.stringify(discoveryOrder)
    )
    console.log('TOPIC ADVANCEMENT - CURRENT INDEX:', currentIndex)
    console.log(
      'TOPIC ADVANCEMENT - NEXT TOPIC SHOULD BE:',
      discoveryOrder[currentIndex + 1]
    )

    // Update the discovery state
    const updatedDiscoveryState = {
      roleType,
      discoveryOrder,
      currentDiscoTopic: nextTopic,
      completedTopics: updatedCompletedTopics
    }

    console.log('TOPIC ADVANCEMENT - NEW STATE:', updatedDiscoveryState)

    // Update the discovery state
    try {
      updateDiscoveryState(updatedDiscoveryState)
      console.log('TOPIC ADVANCEMENT - SUCCESS: State updated')
    } catch (error) {
      console.error('TOPIC ADVANCEMENT - ERROR: Failed to update state', error)
    }

    console.log('TOPIC ADVANCEMENT - COMPLETE:', {
      previousTopic: currentDiscoTopic,
      newTopic: nextTopic,
      timestamp: new Date().toISOString()
    })

    return updatedDiscoveryState
  }

  /**
   * Get user's custom sequences
   * @param {string} userId - User ID
   */
  const getUserSequences = async (userId) => {
    try {
      return await discoveryConfigService.getUserSequences(userId)
    } catch (error) {
      console.error('Error fetching user sequences:', error)
      return []
    }
  }

  /**
   * Create sequence from role type default
   * @param {string} userId - User ID
   * @param {string} roleType - Role type
   * @param {string} customName - Custom sequence name
   */
  const createSequenceFromDefault = async (userId, roleType, customName) => {
    try {
      return await discoveryConfigService.createSequenceFromDefault(userId, roleType, customName)
    } catch (error) {
      console.error('Error creating sequence from default:', error)
      throw error
    }
  }

  return {
    selectedRoleTypes,
    showRoleModal,
    convoStarted,
    setConvoStarted,
    handleStartIntake,
    handleRoleTypeSelect,
    handleStartConversation,
    resetRoleTypeSelection,
    setShowRoleModal,
    roleTypesCache,
    currentDiscoveryState,
    updateDiscoveryState,
    loadSessionState,
    advanceToNextTopic,
    transitionToTopic, // New direct topic transition callback
    // Discovery sequence management
    getUserSequences,
    createSequenceFromDefault,
    discoveryConfigService // Expose the service for direct access
  }
}

export default useRoleTypeManager
